<template>
  <modal
    :name="modalName"
    width="94%"
    height="auto"
    :scrollable="true"
    :clickToClose="false"
    @before-open="preparar"
  >
    <CloseModal :close="fechar" />
    <Loading :isActive="carregando" />

    <div class="modalbox">
      <div class="modalbox-content wizard-conteudo">
        <div class="wizard-cabecalho">
          <div class="wizard-cabecalho__identidade">
            <h4 class="modal-title">{{ titulo }}</h4>
            <CBadge v-if="modoEdicao" :color="form.ativo ? 'success' : 'secondary'">
              {{ form.ativo ? "Ativo" : "Inativo" }}
            </CBadge>
          </div>

          <CButton size="sm" color="link" class="p-0" @click="ajudaAberta = !ajudaAberta">
            <fa-icon icon="lightbulb" class="mr-1" />
            {{ ajudaAberta ? "Ocultar explicação" : "Como funciona" }}
          </CButton>
        </div>

        <WizardEtapas
          :etapas="ETAPAS"
          :etapaAtual="etapaAtual"
          :porEtapa="validacao.porEtapa"
          :visitadas="visitadas"
          :mostrarErros="mostrarErros"
          :navegacaoLivre="modoEdicao"
          @selecionar="irParaEtapa"
        />

        <CCollapse :show="ajudaAberta" class="mt-2">
          <PainelAjuda :etapa="etapaAtual" />
        </CCollapse>

        <div class="mt-3">
          <EtapaIdentificacao v-if="etapaAtual === 1" :form="form" />

          <EtapaProdutos
            v-else-if="etapaAtual === 2"
            :form="form"
            :colunasDisponiveis="colunasDisponiveis"
            :diagnostico="diagnostico"
            :carregando="diagnosticoCarregando"
            :erro="diagnosticoErro"
            @recalcular="calcularDiagnostico"
          />

          <EtapaVariacoes
            v-else-if="etapaAtual === 3"
            :form="form"
            :colunasDisponiveis="colunasDisponiveis"
          />

          <EtapaRevisao
            v-else
            :form="form"
            :colunasDisponiveis="colunasDisponiveis"
            :diagnostico="diagnostico"
            :validacao="validacao"
            :modoEdicao="modoEdicao"
            @ir-para-etapa="irParaEtapa"
          />
        </div>

        <CAlert
          v-if="mostrarErros[etapaAtual] && validacaoAtual.erros.length"
          color="danger"
          class="mt-3"
        >
          <div ref="pendencias">
            <strong>
              <fa-icon icon="exclamation-triangle" class="mr-1" />
              Para avançar, resolva:
            </strong>
            <ul class="mb-0 mt-1 pl-3">
              <li v-for="(erro, indice) in validacaoAtual.erros" :key="`pendencia-${indice}`">
                {{ erro }}
              </li>
            </ul>
          </div>
        </CAlert>
      </div>
    </div>

    <div class="modal-footer-bar wizard-rodape">
        <span class="wizard-rodape__info mr-auto">
          <!-- Formulário em branco não é formulário com erro: a contagem de
               pendências só aparece depois de tentar avançar (ou na edição, em
               que os dados já existem). -->
          <template v-if="validacaoAtual.erros.length">
            <span v-if="mostrarErros[etapaAtual]" class="text-danger font-weight-bold">
              {{ validacaoAtual.erros.length }}
              {{ validacaoAtual.erros.length === 1 ? "pendência" : "pendências" }} nesta etapa
            </span>
          </template>
          <template v-else-if="validacaoAtual.avisos.length">
            <span class="text-warning">
              {{ validacaoAtual.avisos.length }}
              {{ validacaoAtual.avisos.length === 1 ? "recomendação" : "recomendações" }}
              — não impede avançar
            </span>
          </template>
          <template v-else>
            <span class="text-success font-weight-bold">
              <fa-icon icon="check" class="mr-1" />
              Etapa completa
            </span>
          </template>
        </span>

        <CButton color="secondary" variant="outline" @click="fechar">Cancelar</CButton>
        <CButton v-if="etapaAtual > 1" color="primary" variant="outline" @click="voltar">
          <fa-icon icon="arrow-left" class="mr-1" />
          Voltar
        </CButton>
        <CButton v-if="etapaAtual < ULTIMA_ETAPA" color="primary" @click="avancar">
          Avançar
        </CButton>
        <CButton v-else color="primary" :disabled="salvando" @click="salvar">
          <CSpinner v-if="salvando" size="sm" class="mr-1" />
          {{ modoEdicao ? "Salvar alterações" : "Criar grupo de grade" }}
        </CButton>
    </div>
  </modal>
</template>

<script>
import debounce from "just-debounce-it";
import { get, post, put } from "@/helpers/apiRequest";
import CloseModal from "@/components/CloseModal.vue";
import Loading from "@/components/Loading.vue";
import WizardEtapas from "./wizard/WizardEtapas.vue";
import PainelAjuda from "./wizard/PainelAjuda.vue";
import EtapaIdentificacao from "./wizard/EtapaIdentificacao.vue";
import EtapaProdutos from "./wizard/EtapaProdutos.vue";
import EtapaVariacoes from "./wizard/EtapaVariacoes.vue";
import EtapaRevisao from "./wizard/EtapaRevisao.vue";
import { ETAPAS, ULTIMA_ETAPA, validarEtapa, validarFormulario } from "./wizard/etapas";
import { formularioVazio, paraFormulario, paraPayload } from "./wizard/adaptadores";

export default {
  name: "ModalWizardGrupoGrade",
  components: {
    CloseModal,
    Loading,
    WizardEtapas,
    PainelAjuda,
    EtapaIdentificacao,
    EtapaProdutos,
    EtapaVariacoes,
    EtapaRevisao,
  },
  props: {
    /** Grupo vindo da listagem (com campos/regras) ou null para um cadastro novo. */
    item: { type: Object, default: null },
    close: { type: Function, required: true },
  },
  data() {
    return {
      ETAPAS,
      ULTIMA_ETAPA,
      modalName: "ModalGrupoGrade",
      etapaAtual: 1,
      visitadas: [1],
      ajudaAberta: false,
      // Erros só aparecem depois de o usuário tentar avançar: um formulário que
      // reclama antes de ser preenchido treina o usuário a ignorar alerta.
      mostrarErros: {},
      form: formularioVazio(),
      colunasDisponiveis: [],
      diagnostico: null,
      diagnosticoCarregando: false,
      diagnosticoErro: null,
      carregando: false,
      salvando: false,
      sujo: false,
      salvo: false,
    };
  },
  computed: {
    modoEdicao() {
      return !!this.form.id;
    },
    titulo() {
      if (!this.modoEdicao) return "Novo grupo de grade";
      return this.form.nome ? `Editar: ${this.form.nome}` : "Editar grupo de grade";
    },
    validacao() {
      return validarFormulario(this.form, this.diagnostico, this.colunasDisponiveis);
    },
    validacaoAtual() {
      // A revisão não tem campo próprio: o rodapé dela fala pelo formulário todo.
      if (this.etapaAtual === ULTIMA_ETAPA) {
        return { erros: this.validacao.erros, avisos: this.validacao.avisos };
      }

      return (
        this.validacao.porEtapa[this.etapaAtual] ||
        validarEtapa(this.etapaAtual, this.form, this.diagnostico, this.colunasDisponiveis)
      );
    },
    regrasValidas() {
      return (this.form.regras || []).filter(
        (regra) => regra.colunaProduto && String(regra.valor || "").trim()
      );
    },
    /**
     * Chave primitiva das condições completas. Observar a lista em si dispararia
     * o recálculo a cada condição vazia adicionada, porque a computada devolve
     * um array novo mesmo quando o conteúdo é o mesmo.
     */
    chaveRegras() {
      return JSON.stringify(
        this.regrasValidas.map((regra) => [regra.colunaProduto, String(regra.valor)])
      );
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.sujo = true;
      },
    },
    chaveRegras() {
      // As condições mudaram: o alcance na tela está velho até o retorno.
      this.diagnosticoCarregando = true;
      this.calcularDiagnosticoComAtraso();
    },
  },
  created() {
    // Cada tecla nas condições mexeria numa contagem que varre o catálogo.
    this.calcularDiagnosticoComAtraso = debounce(this.calcularDiagnostico, 600);
  },
  methods: {
    /** O modal é reaproveitado entre aberturas: todo estado volta ao início aqui. */
    preparar() {
      this.mostrarErros = {};
      this.ajudaAberta = false;
      this.diagnostico = null;
      this.diagnosticoErro = null;
      this.diagnosticoCarregando = false;
      this.salvando = false;
      this.salvo = false;

      if (!this.colunasDisponiveis.length) {
        this.carregarColunas();
      }

      // `before-open` do vue-js-modal dispara ANTES do re-render do pai que
      // atualiza os props: ler `item` aqui pegaria o valor da abertura anterior
      // (na primeira edição, null — e o modal abria como "Novo grupo").
      this.$nextTick(() => {
        const editando = !!(this.item && this.item.id);

        this.form = editando ? paraFormulario(this.item) : formularioVazio();

        // O watch de `chaveRegras` sozinho não cobre a reabertura: o modal é
        // reaproveitado e o form não é zerado ao fechar, então abrir de novo o
        // mesmo grupo (ou outro com as mesmas condições) mantém a chave
        // idêntica, o watch não dispara e o alcance fica vazio para sempre —
        // logo depois de `preparar()` ter limpado o diagnóstico. Disparar aqui
        // não duplica chamada: as duas entradas caem no mesmo debounce.
        if (this.regrasValidas.length) {
          this.diagnosticoCarregando = true;
          this.calcularDiagnosticoComAtraso();
        }

        // Cadastro novo começa no passo 1. Edição abre no resumo: quem já tem o
        // grupo configurado quer ver o que está valendo e pular para o ponto
        // certo, não repetir as quatro etapas. Marcar todas como visitadas faz a
        // barra de etapas mostrar em vermelho o que estiver inválido no grupo.
        this.etapaAtual = editando ? ULTIMA_ETAPA : 1;
        this.visitadas = editando ? ETAPAS.map((etapa) => etapa.numero) : [1];

        // Num grupo que já existe, apontar o que está inválido é útil de imediato.
        // Num cadastro novo, seria reclamar de campo que ninguém preencheu ainda.
        if (editando) {
          ETAPAS.forEach(({ numero }) => this.$set(this.mostrarErros, numero, true));
        }

        // Trocar o form marca a tela como suja pelo watch; isso não é edição do
        // usuário, então o estado volta a limpo logo depois.
        this.$nextTick(() => {
          this.sujo = false;
        });
      });
    },
    carregarColunas() {
      get("/admin/grupo-grade/colunas-disponiveis")
        .then((json) => {
          this.colunasDisponiveis = json.data || json || [];
        })
        .catch(() => {
          this.$swal({
            title: "Não foi possível carregar os atributos de produto",
            text: "Feche e abra o cadastro para tentar de novo.",
            icon: "error",
          });
        });
    },
    calcularDiagnostico() {
      if (!this.regrasValidas.length) {
        this.diagnostico = null;
        this.diagnosticoErro = null;
        this.diagnosticoCarregando = false;
        return;
      }

      this.diagnosticoCarregando = true;
      this.diagnosticoErro = null;

      post("/admin/grupo-grade/diagnostico", { regras: this.regrasValidas })
        .then((json) => {
          this.diagnostico = json.data || json;
        })
        .catch((err) => {
          // Sem isto a etapa fica no "Contando os produtos..." para sempre:
          // diagnóstico nulo é indistinguível de cálculo em andamento.
          this.diagnostico = null;
          this.diagnosticoErro =
            err?.message || "Não foi possível calcular o alcance destas condições.";
        })
        .finally(() => {
          this.diagnosticoCarregando = false;
        });
    },
    marcarVisitada(numero) {
      if (!this.visitadas.includes(numero)) {
        this.visitadas.push(numero);
      }
    },
    avancar() {
      if (this.validacaoAtual.erros.length) {
        this.$set(this.mostrarErros, this.etapaAtual, true);
        this.$nextTick(() => {
          if (this.$refs.pendencias) {
            this.$refs.pendencias.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
        return;
      }

      this.irParaEtapa(this.etapaAtual + 1);
    },
    voltar() {
      this.irParaEtapa(this.etapaAtual - 1);
    },
    irParaEtapa(numero) {
      if (numero < 1 || numero > ULTIMA_ETAPA) return;
      this.marcarVisitada(numero);
      this.etapaAtual = numero;

      // O scroll é do corpo do modal, não da janela.
      const conteudo = this.$el.querySelector(".wizard-conteudo");
      if (conteudo) conteudo.scrollTop = 0;
    },
    salvar() {
      ETAPAS.forEach(({ numero }) => this.marcarVisitada(numero));

      if (!this.validacao.valido) {
        this.$swal({
          title: "Ainda falta resolver algumas pendências",
          text: "Veja a lista no topo desta etapa e corrija antes de salvar.",
          icon: "warning",
        });
        return;
      }

      const payload = paraPayload(this.form);
      const requisicao = this.modoEdicao
        ? put(`/admin/grupo-grade/${this.form.id}`, payload)
        : post("/admin/grupo-grade", payload);

      this.salvando = true;
      requisicao
        .then(() => {
          this.salvo = true;
          return this.$swal({
            title: this.modoEdicao ? "Grupo atualizado!" : "Grupo de grade criado!",
            text: "Os canais passam a usar esta configuração em até 10 minutos.",
            icon: "success",
            timer: 2200,
            showConfirmButton: false,
          });
        })
        .then(() => {
          if (this.salvo) this.close();
        })
        .catch((erro) => {
          this.$swal({
            title: this.modoEdicao ? "Falha ao atualizar" : "Falha ao criar o grupo",
            text: erro.message || "Verifique os dados e tente novamente.",
            icon: "error",
          });
        })
        .finally(() => {
          this.salvando = false;
        });
    },
    fechar() {
      if (!this.sujo || this.salvo) {
        this.$modal.hide(this.modalName);
        return;
      }

      this.$swal({
        title: "Sair sem salvar?",
        text: "As alterações feitas neste cadastro serão perdidas.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sair sem salvar",
        cancelButtonText: "Continuar editando",
      }).then((resposta) => {
        if (resposta.value) this.$modal.hide(this.modalName);
      });
    },
  },
};
</script>

<style scoped>
/* O corpo cresce com o conteúdo até o teto de 90vh menos o padding do .modalbox
   e a barra de ações — etapa curta não deixa meia tela em branco, etapa longa
   rola internamente. Não depende do max-height de components.css.

   overflow-x: hidden é obrigatório aqui: com overflow-y auto o navegador
   promove o overflow-x a auto, e as margens negativas de .row (-15px) passariam
   a render uma barra horizontal em qualquer largura de tela. */
.wizard-conteudo {
  min-height: 260px;
  max-height: calc(90vh - 130px);
  overflow-y: auto;
  overflow-x: hidden;
}

/* O padding de 32px do .modalbox come largura demais no celular. */
@media (max-width: 767.98px) {
  .modalbox {
    padding: 16px;
  }

  .wizard-conteudo {
    max-height: calc(90vh - 100px);
  }
}

.wizard-cabecalho {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.wizard-cabecalho__identidade {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

/* Sem a cor explícita o título herda o $body-color do CoreUI (#4f5d73) e fica
   lavado ao lado dos rótulos. Mesma cor que o modal antigo do módulo usava. */
.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d2939;
  margin: 0;
}

/* Mesma barra de ações das outras modais de cadastro do painel. */
.wizard-rodape {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #fff;
}

.wizard-rodape__info {
  font-size: 13px;
}
</style>
